<template>
  <div class="listing-loader">
    <transition name="fade">
      <div
        v-if="restoring"
        class="restoring"
      >
        Restoring scroll position...
      </div>
    </transition>

    <div v-if="startingPage > 1">
      <a
        href="#"
        @click.prevent="resetPages()"
      >
        Load feed start
      </a>
      <a
        href="#"
        @click.prevent="loadPage(startingPage - 1)"
      >
        Load page {{ startingPage - 1 }}
      </a>
    </div>

    <new-post-card
      v-if="userData && showNewPostCard && !no_zipcode"
      :group="group"
    />

    <template v-if="loading">
      <div
        class="alert alert-secondary"
        role="alert"
      >
        Loading...
      </div>
    </template>

    <template
      v-if="no_zipcode"
    >
      <h3>
        Enter in a zip code and distance preference to get started!
      </h3>
      <customize-feed-form />
    </template>

    <template v-else-if="feedItems">
      <template v-if="feedItems.length">
        <template
          v-if="!userData"
        >
          <h2>Default Feed</h2>
          <p>
            If you would like to customize
            your feed and be able to post your own content. Please
            <router-link :to="{ name: 'registration' }">
              sign up
            </router-link>
            first.
          </p>
        </template>

        <listing-expanded
          v-for="(item, itemIndex) of feedItems"
          :id="`listing_${item.id}`"
          :key="`item${itemIndex}`"
          :item="item"
        />
        <template v-if="!next_page">
          <div
            class="alert alert-secondary"
            style="margin-top: 20px"
            role="alert"
          >
            {{ end_of_feed[0] }}
            <router-link
              v-if="end_of_feed[1]"
              :to="{ name: end_of_feed[1] }"
              style="color: var(--blue)"
            >
              here
            </router-link>
          </div>
        </template>
      </template>
      <template v-else-if="userData && search">
        <div
          class="alert alert-secondary"
          role="alert"
        >
          No search matches, please try a new search term or use the
          <router-link
            :to="{ name: 'add_search' }"
            style="color: var(--blue)"
          >
            advanced search
          </router-link>
        </div>
      </template>
      <template v-else-if="userData">
        <div
          class="alert alert-secondary"
          role="alert"
        >
          {{ end_of_feed[0] }}
          <router-link
            v-if="end_of_feed[1]"
            :to="{ name: end_of_feed[1] }"
            style="color: var(--blue)"
          >
            here.
          </router-link>
        </div>
      </template>
    </template>

    <spinner
      v-else-if="loading"
      preset="large"
    />
    <template v-else-if="error">
      <div
        class="alert alert-danger"
        role="alert"
      >
        An error occurred while trying to load your feed. Please check your
        connection and try again.
      </div>

      <button
        type="button"
        class="btn btn-outline-primary"
        @click="loadFeedItems"
      >
        Try Again
      </button>
    </template>
    <template v-else-if="unauthorized">
      <div
        class="alert alert-danger"
        role="alert"
      >
        Your account has been deactivated due to the violation of one or more
        of the sites rules.
      </div>
    </template>

    <div
      ref="bottom-area"
      class="mt"
    >
      <spinner
        v-if="bottom_loading && !loading"
        preset="large"
      />
      <template v-else-if="bottom_error">
        An error occurred while trying to load your feed. Please check your
        connection and try again.
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import CustomizeFeedForm from '@/components/CustomizeFeedForm.vue';
import ListingExpanded from '@/components/ListingExpanded.vue';
import NewPostCard from '@/components/NewPostCard.vue';

export default {
  components: {
    CustomizeFeedForm,
    ListingExpanded,
    NewPostCard,
  },
  props: {
    group: { type: Number, default: null },
    membership: { type: Boolean, default: false },
    page: { type: String, default: null },
    user: { type: Number, default: null },
    category: { type: Number, default: null },
    tags: { type: Array, default: null },
    searchId: { type: String, default: null },
    tagged: { type: String, default: null },
    showNewPostCard: { type: Boolean, default: true },
  },
  data() {
    return {
      focused: null,
      startingPage: 1,
      pageNumber: 1,
      loading: false,
      error: false,
      bottom_loading: false,
      bottom_error: false,
      unauthorized: false,
      next_page: true,
      restoring: false,
    };
  },
  computed: {
    ...mapState([
      'feedItems',
    ]),
    search() {
      return this.$route.query.search;
    },
    no_zipcode() {
      return this.userData && this.userData.zip_code === null;
    },
    end_of_feed() {
      if (['home', 'sold_listings', 'looking', 'all_listings', 'listing_category', 'tagged'].includes(this.$route.name)) {
        return ['End of feed. To see more, change your distance filter', 'update_profile'];
      }

      if (['profile', 'user_listings'].includes(this.$route.name)) {
        if (this.user === this.userData.id) {
          return ['You have no more posts.', null];
        }

        return ['User has no more posts.', null];
      }

      if (this.$route.name === 'favorites') {
        return ['You have no more favorited posts.', null];
      }

      if (this.$route.name === 'group_detail') {
        if (!this.membership) {
          return ['You cannot see posts until you are an accepted member of the group.'];
        }
        return ['End of groups content.', null];
      }

      return ['End of feed', null];
    },
  },
  watch: {
    group() {
      this.resetPages();
    },
    userData(newValue) {
      if (newValue !== null) {
        this.resetPages();
      }
    },
    category() {
      this.resetPages();
    },
    tags() {
      this.resetPages();
    },
    tagged() {
      this.resetPages();
    },
    page() {
      this.loadFeedItems();
    },
    search() {
      this.resetPages();
    },
    user() {
      this.resetPages();
    },
  },
  async mounted() {
    // restore
    const page = this.$route.query.page;
    const listing = this.$route.query.listing;

    if (page || listing) {
      this.restoring = true;
      this.next_page = true;
      this.pageNumber = (parseInt(page, 10) || 1);
      this.startingPage = this.pageNumber;
      await this.loadPage(this.pageNumber);

      const el = document.getElementById(`listing_${listing}`);
      if (el) {
        await new Promise(r => setTimeout(r, 500));
        el.scrollIntoView({
          block: 'center',
        });
      }
      this.restoring = false;
    } else {
      await this.loadFeedItems();
    }

    this.scroller = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.scroll();
      }, 16);
    };
    window.addEventListener('scroll', this.scroller);
  },
  destroyed() {
    window.removeEventListener('scroll', this.scroller);
  },
  methods: {
    async scroll() {
      const scrollTop = Math.max(
        window.pageYOffset,
        document.documentElement.scrollTop,
        document.body.scrollTop,
      );
      const bottomOfWindow = (
        scrollTop + window.innerHeight * 2
        >= document.documentElement.offsetHeight
      );
      if (bottomOfWindow) {
        await this.loadFeedItems();
      }

      // find first visible feed item
      const first = this.feedItems.find((item) => {
        const el = document.getElementById(`listing_${item.id}`);
        if (el) {
          const box = el.getBoundingClientRect();
          if (box.y > -box.height * 0.4) {
            return true;
          }
        }
        return false;
      });

      // recognize the first visible
      this.focused = first ? first.id : null;

      // save what the user is looking at
      this.$router.replace({
        query: {
          search: this.search,
          page: first ? first.fromPage : this.pageNumber - 1,
          listing: first ? first.id : '',
        },
      }).catch(() => null);
    },
    async resetPages() {
      this.pageNumber = 1;
      this.startingPage = 1;
      this.next_page = true;
      this.loadPage(1);
    },
    async loadPage(page) {
      const hasNextPage = page === 1 || page !== this.pageNumber || this.next_page;
      if (!this.loading && hasNextPage) {
        this.loading = true;

        if (page === 1) {
          // first page? reset
          this.$store.dispatch('set_feed_items', []);
          this.startingPage = page;
          this.pageNumber = 1;
        } else {
          // continue, assuming they are scrolling
          this.bottom_error = false;
          this.bottom_loading = true;
          await this.$nextTick();
          const bottomArea = this.$refs['bottom-area'];

          if (page >= this.pageNumber) {
            bottomArea.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }

        const query = {};
        if (this.group) {
          query.group = this.group;
        }
        if (this.page) {
          query.view = this.page;
        }
        if (this.user) {
          query.user = this.user;
        }
        if (this.category) {
          query.category = this.category;
        }
        if (this.tags) {
          query.tags = this.tags;
        }
        if (this.tagged) {
          query.tagged = this.tagged;
        }
        if (this.search) {
          query.search = this.search;
        }
        if (this.searchId) {
          query.searchId = this.searchId;
          const freeText = await this.getSearchFreeText();
          if (freeText) {
            query.search = freeText;
          }
        }
        query.page = page;

        let url = 'posts/';
        if (Object.keys(query).length > 0) {
          const params = Object.keys(query).map(k => `${k}=${query[k]}`);
          url = `${url}?${params.join('&')}`;
        }

        const responseData = await this.api({ url, method: 'GET' });

        if (responseData.status === 200) {
          const results = responseData.body.results.map((item) => {
            // eslint-disable-next-line no-param-reassign
            item.fromPage = page;
            return item;
          });

          // check for backwards paging
          if (page <= this.startingPage) {
            this.$store.dispatch('set_feed_items', results.concat(this.feedItems));
            this.startingPage = page;
          } else {
            this.$store.dispatch('push_feed_items', results);
            this.next_page = responseData.body.next;
          }

          // check for forward paging
          if (page === this.pageNumber) {
            this.pageNumber += 1;
          }
          this.incrementViewCount(results);
        } else if (responseData.status === 404) {
          this.next_page = false;
        } else if (responseData.status === 401) {
          this.unauthorized = true;
        } else {
          this.error = true;
          if (page > 1) {
            this.bottom_error = true;
          }
        }

        this.loading = false;
        this.bottom_loading = false;
      }
    },
    async loadFeedItems() {
      clearTimeout(this.loadFeedItemsTimeout);
      this.loadFeedItemsTimeout = setTimeout(
        () => this.loadPage(this.pageNumber),
        100,
      );
    },
    async getSearchFreeText() {
      const responseData = await this.api({ url: `searches/${this.searchId}/` });

      if (responseData.status === 200) {
        return responseData.body.free_text;
      }

      this.error = true;
      return null;
    },
    async incrementViewCount(posts) {
      posts.forEach(async (post) => {
        try {
          await this.api({
            url: `posts/${post.id}/view/`,
            method: 'POST',
          });
        } catch (e) {} // eslint-disable-line no-empty
      });
    },
  },
};
</script>

<style scoped>
.restoring {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 15px;
  background: #ddd;
  z-index: 100;
  text-align: center;
  font-weight: bold;
  border-top: 2px solid #aaa;
}
</style>
